import React from 'react'
import {
  StatusChipV4,
  StatusChipBg,
  StatusChipText,
} from '@labrav/react-components'
import { OfferStatus as OfferStatusEnum } from '../../../modules/privateOffer/reducer'
import type { CloudMarketplace } from '../../../types/index'
import { getStatusAndLabelForPrivateOffer } from '../../utils/getStatusAndLabelForPrivateOffer'

type StatusChipProp = {
  variant?: 'default' | 'error' | 'success' | 'info' | 'warning'
  bgColor: StatusChipBg
  textColor?: StatusChipText
  borderColor?: StatusChipBg
  label: string
}

export type OfferStatusProps = {
  status: OfferStatusEnum | string
  cloud?: CloudMarketplace
}

const statusMap: Record<string, Omit<StatusChipProp, 'label'>> = {
  OFFER_CREATION_IN_PROGRESS: {
    variant: 'default',
    bgColor: StatusChipBg.YELLOW,
    textColor: StatusChipText.YELLOW,
    borderColor: StatusChipBg.YELLOW,
  },
  OFFER_RESTRICTION_IN_PROGRESS: {
    variant: 'default',
    bgColor: StatusChipBg.RED,
    borderColor: StatusChipBg.RED,
  },
  OFFER_CREATION_FAILED: {
    variant: 'error',
    bgColor: StatusChipBg.RED,
    borderColor: StatusChipBg.RED,
  },
  OFFER_TERMS_ACCEPTED_BUYER: {
    variant: 'default',
    bgColor: StatusChipBg.YELLOW,
    textColor: StatusChipText.YELLOW,
    borderColor: StatusChipBg.YELLOW,
  },
  OFFER_IN_DRAFT: {
    variant: 'default',
    textColor: StatusChipText.GRAY,
    bgColor: StatusChipBg.GRAY,
    borderColor: StatusChipBg.GRAY,
  },
  OFFER_EMAIL_SENT: {
    variant: 'success',
    textColor: StatusChipText.GREEN,
    bgColor: StatusChipBg.GREEN,
    borderColor: StatusChipBg.GREEN,
  },
  OFFER_VIEWED: {
    variant: 'default',
    bgColor: StatusChipBg.GREEN,
    textColor: StatusChipText.GREEN,
    borderColor: StatusChipBg.GREEN,
  },
  OFFER_ACCEPTED: {
    variant: 'default',
    bgColor: StatusChipBg.GREEN,
  },
  OFFER_EXPIRED: {
    variant: 'default',
    bgColor: StatusChipBg.RED,
    borderColor: StatusChipBg.RED,
  },
  OFFER_FAILED: {
    variant: 'default',
    bgColor: StatusChipBg.RED,
  },
  PRIVATE_OFFER_VIEWED: {
    variant: 'default',
    textColor: StatusChipText.LIGHTBLUE,
    bgColor: StatusChipBg.LIGHTBLUE,
    borderColor: StatusChipBg.LIGHTBLUE,
  },
  PRIVATE_OFFER_CREATION_IN_PROGRESS: {
    variant: 'default',
    bgColor: StatusChipBg.YELLOW,
    textColor: StatusChipText.YELLOW,
    borderColor: StatusChipBg.YELLOW,
  },
  PRIVATE_OFFER_EMAIL_SENT: {
    variant: 'success',
    textColor: StatusChipText.GREEN,
    bgColor: StatusChipBg.GREEN,
    borderColor: StatusChipBg.GREEN,
  },
  PRIVATE_OFFER_ACCEPTED: {
    variant: 'default',
    bgColor: StatusChipBg.GREEN,
  },
  PRIVATE_OFFER_EXPIRED: {
    variant: 'default',
    bgColor: StatusChipBg.RED,
  },
  PRIVATE_OFFER_FAILED: {
    variant: 'error',
    bgColor: StatusChipBg.RED,
    borderColor: StatusChipBg.RED,
  },
  PRIVATE_OFFER_IN_DRAFT: {
    variant: 'default',
    textColor: StatusChipText.GRAY,
    bgColor: StatusChipBg.GRAY,
    borderColor: StatusChipBg.GRAY,
  },
  PRIVATE_OFFER_RESTRICTION_IN_PROGRESS: {
    variant: 'default',
    bgColor: StatusChipBg.RED,
    borderColor: StatusChipBg.RED,
  },
  PRIVATE_OFFER_RESTRCITED: {
    variant: 'default',
    bgColor: StatusChipBg.RED,
    borderColor: StatusChipBg.RED,
  },
  OFFER_PURCHASE_COMPLETE: {
    variant: 'default',
    textColor: StatusChipText.GREEN,
    bgColor: StatusChipBg.GREEN,
    borderColor: StatusChipBg.GREEN,
  },
  OFFER_RESTRICTED: {
    variant: 'default',
    bgColor: StatusChipBg.RED,
    borderColor: StatusChipBg.RED,
  },
}

export const OfferStatusChip: React.FC<OfferStatusProps> = ({
  status,
  cloud,
}) => {
  const statusString =
    typeof status === 'string' ? status : (status as OfferStatusEnum)
  const statusConfig = statusMap[statusString]

  if (!statusConfig) {
    return null
  }

  const statusDetails = getStatusAndLabelForPrivateOffer(
    status as OfferStatusEnum,
    cloud
  )

  return (
    <StatusChipV4
      {...statusDetails}
      variant={statusConfig.variant}
      bgColor={statusConfig.bgColor}
      textColor={statusConfig.textColor ?? statusDetails.textColor}
      borderColor={statusConfig.borderColor}
    />
  )
}
